import { useState } from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { BbotButton, BbotTag, SettingsCard } from "top-component-library";
import MasterSyncDialog from "../MasterSyncDialog";
import OmnivoreConnectDialog from "../OmnivoreConnectDialog";
import OmnivoreMmsDialog from "../OmnivoreMmsDialog";
import { INTEGRATION_CATALOG } from "../constants";
import { SettingControlRow } from "../styles";

// Mirrors settings.html's "Sync from Master Account" / "POS Integrations" category
// blocks. The two are mutually exclusive: has_connected_pos_integration is only true once
// some *other* external_source is active, which is exactly when MasterSync's row is
// hidden - so this never needs to render both at once.
//
// Deviation from legacy: the "POS Integrations" section's visibility check
// (settingsController.js's showOmnivoreIntegrations) only tests for 'omnivore'/
// 'omnivore_direct', never 'omnivore_3', even though omnivore_3's connect/disconnect/
// status endpoints and dialog branch are otherwise fully implemented - a customer with
// only Omnivore 3 connected can never reach it today. Fixed here (checks all 3 keys)
// since it's a one-line, purely-additive fix with no effect unless that specific case
// applies.
const POS_INTEGRATION_KEYS = ["omnivore_3", "omnivore_direct", "omnivore"];

const IntegrationsCard = ({ isAdmin, connectedIntegrations, hasConnectedPosIntegration, remnant }) => {
  const [masterSyncVisible, setMasterSyncVisible] = useState(false);
  const [omnivoreDialogVisible, setOmnivoreDialogVisible] = useState(false);

  const activePosIntegrationKey = POS_INTEGRATION_KEYS.find((key) => key in connectedIntegrations) ?? null;

  // A diagnostic poll outlives the dialog that started it (the hook, not the dialog, owns
  // it), so closing the dialog has to explicitly cancel any diagnostic still in flight.
  const closeOmnivoreDialog = () => {
    setOmnivoreDialogVisible(false);
    remnant.stopDiagnostic();
  };

  return (
    <>
      {!hasConnectedPosIntegration && (
        <SettingsCard title="Sync from Master Account">
          <p>
            Sync menus and other settings from another Bbot account to simplify setup and management of multiple
            accounts.
          </p>
          {"master_account" in connectedIntegrations ? (
            <SettingControlRow>
              <BbotTag color="success">
                {INTEGRATION_CATALOG.master_account.connectedButtonText} <CheckCircleOutlined />
              </BbotTag>
              <BbotButton type="link" onClick={() => setMasterSyncVisible(true)}>
                Change Settings
              </BbotButton>
            </SettingControlRow>
          ) : (
            <SettingControlRow>
              <BbotButton type="primary" onClick={() => setMasterSyncVisible(true)}>
                {INTEGRATION_CATALOG.master_account.buttonText}
              </BbotButton>
            </SettingControlRow>
          )}
          <MasterSyncDialog
            visible={masterSyncVisible}
            isAdmin={isAdmin}
            allowedCustomers={remnant.allowedCustomers}
            settings={remnant.masterSync}
            onClose={() => setMasterSyncVisible(false)}
            onLoad={remnant.loadMasterSync}
            onSave={remnant.saveMasterSync}
          />
        </SettingsCard>
      )}
      {activePosIntegrationKey && (
        <SettingsCard title="POS Integrations">
          <p>{INTEGRATION_CATALOG[activePosIntegrationKey].title}</p>
          <SettingControlRow>
            <BbotTag color="success">
              Connected <CheckCircleOutlined />
            </BbotTag>
            <BbotButton type="link" onClick={() => setOmnivoreDialogVisible(true)}>
              Change Settings
            </BbotButton>
          </SettingControlRow>
          {activePosIntegrationKey === "omnivore" && (
            <OmnivoreMmsDialog
              visible={omnivoreDialogVisible}
              isAdmin={isAdmin}
              settings={remnant.omnivoreMms}
              onClose={closeOmnivoreDialog}
              onLoad={remnant.loadOmnivoreMms}
              onSave={remnant.connectOmnivoreMms}
              onDisconnect={remnant.disconnectOmnivoreMms}
              onCheckDiagnostic={remnant.checkOmnivoreMmsDiagnostic}
              onDownloadPosItems={remnant.downloadOmnivoreMmsPosItems}
            />
          )}
          {activePosIntegrationKey === "omnivore_direct" && (
            <OmnivoreConnectDialog
              visible={omnivoreDialogVisible}
              variant="direct"
              isAdmin={isAdmin}
              settings={remnant.omnivoreDirect}
              locationOptions={remnant.locationOptions}
              isLoadingLocationOptions={remnant.isLoadingLocationOptions}
              onClose={closeOmnivoreDialog}
              onLoad={remnant.loadOmnivoreDirect}
              onLocationIdChange={remnant.loadLocationOptions}
              onSave={remnant.connectOmnivoreDirect}
              onDisconnect={remnant.disconnectOmnivoreDirect}
              onCheckDiagnostic={remnant.checkOmnivoreDirectDiagnostic}
              onDownloadPosItems={remnant.downloadOmnivoreDirectPosItems}
            />
          )}
          {activePosIntegrationKey === "omnivore_3" && (
            <OmnivoreConnectDialog
              visible={omnivoreDialogVisible}
              variant="omnivore3"
              isAdmin={isAdmin}
              settings={remnant.omnivore3}
              locationOptions={remnant.locationOptions}
              isLoadingLocationOptions={remnant.isLoadingLocationOptions}
              onClose={closeOmnivoreDialog}
              onLoad={remnant.loadOmnivore3}
              onLocationIdChange={remnant.loadLocationOptions}
              onSave={remnant.connectOmnivore3}
              onDisconnect={remnant.disconnectOmnivore3}
            />
          )}
        </SettingsCard>
      )}
    </>
  );
};

IntegrationsCard.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  connectedIntegrations: PropTypes.object.isRequired,
  hasConnectedPosIntegration: PropTypes.bool.isRequired,
  remnant: PropTypes.object.isRequired,
};

export default IntegrationsCard;
